import { cloneDeep } from 'lodash-es'
import type {
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import type { BaseTool } from '@/plugins/cad/module/core/tool/BaseTool'

/**
 * 鼠标位置，单位屏幕像素。
 */
export interface MousePosition {
  /** 屏幕 X 坐标 */
  x: number
  /** 屏幕 Y 坐标 */
  y: number
}

/**
 * 探测器行为开关配置。
 */
export interface DetectorOptions {
  /** 是否启用探测 */
  enableDetect: boolean
  /** 是否在探测命中时启用高亮 */
  enableHighlight?: boolean
}

/** 探测器事件处理接口，工具可实现此接口对探测结果进行过滤 */
export interface DetectorEventHandler<T> {
  /** 对探测结果进行过滤并返回新的集合 */
  onDetectorFilter?: (entities: T[]) => T[]
}

// 简单的 merge 实现
function merge<T extends object>(target: T, source: Partial<T>): T {
  return Object.assign(target, source)
}

/**
 * 实体探测器抽象基类，基于鼠标位置在场景中寻找命中的实体。
 *
 * @typeParam T - 被探测的实体类型
 */
export abstract class Detector<T> implements MouseEventInfoHandler {
  protected entities: T[] = []

  protected defaultSetting: DetectorOptions = {
    enableDetect: true,
    enableHighlight: false,
  }

  protected setting: DetectorOptions = {
    enableDetect: true,
    enableHighlight: false,
  }

  /** 当前宿主工具实例 */
  tool: BaseTool | undefined

  /**
   * 根据鼠标位置执行探测并缓存结果。
   *
   * @param mousePosition - 屏幕坐标，传 undefined 时跳过探测
   * @returns 过滤后命中的实体列表；未启用或未命中时返回空数组
   */
  handleDetect(mousePosition: MousePosition | undefined) {
    if (!mousePosition) {
      return []
    }
    if (!this.setting.enableDetect) {
      return []
    }

    const result = this.detect(mousePosition)
    this.entities = this.applyDetectorFilter(result)

    return this.entities
  }

  /**
   * 子类实现的具体探测逻辑。
   *
   * @param mousePosition - 屏幕坐标
   * @returns 命中的实体列表，无命中时返回空数组
   */
  abstract detect(mousePosition: MousePosition): T[]

  /**
   * 获取上一次探测命中的实体集合。
   *
   * @returns 命中实体数组，未探测或无命中时返回空数组
   */
  getEntities() {
    return this.entities
  }

  /**
   * 获取上一次探测命中的首个实体。
   *
   * @returns 首个命中实体；无命中时返回 null
   */
  getOneEntity() {
    if (this.entities.length) {
      return this.entities[0]
    }
    return null
  }

  /**
   * 判断上一次探测是否存在命中。
   *
   * @returns 存在命中返回 true，否则返回 false
   */
  isDetected() {
    return this.entities.length > 0
  }

  /**
   * 合并更新当前探测器配置。
   *
   * @param option - 需要覆盖的配置字段
   */
  config(option: Partial<DetectorOptions>) {
    merge(this.setting, option)
  }

  /** 将配置重置为默认值 */
  resetConfig() {
    this.setting = cloneDeep(this.defaultSetting)
  }

  /**
   * 设置默认配置，同时将当前配置重置为默认值。
   *
   * @param option - 新的默认配置
   */
  setDefaultConfig(option: DetectorOptions) {
    Object.assign(this.defaultSetting, option)
    this.resetConfig()
  }

  /**
   * 获取当前是否启用命中高亮。
   *
   * @returns 启用返回 true，否则返回 false 或 undefined
   */
  getEnableHighlight() {
    return this.setting.enableHighlight
  }

  /**
   * 对探测结果执行工具层过滤。
   *
   * @param entities - 探测得到的原始实体列表
   * @returns 过滤后的实体列表；宿主工具未实现过滤钩子时原样返回
   */
  applyDetectorFilter(entities: T[]) {
    const tool = this.tool as DetectorEventHandler<T> | undefined
    if (tool?.onDetectorFilter) {
      return tool.onDetectorFilter(entities)
    }
    return entities
  }

  /************ 鼠标键盘事件处理 ************/
  /**
   * 鼠标移动时触发，基于屏幕位置执行探测。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    this.handleDetect(params.screenPos)
  }

  /************ 注入工具 ************/
  /**
   * 注入宿主工具实例。
   *
   * @param tool - 当前使用该探测器的工具
   */
  injectTool(tool: BaseTool): void {
    this.tool = tool
  }

  /** 解除宿主工具的引用 */
  ejectTool(): void {
    this.tool = undefined
  }
}
